// Eén klein ophaalpatroon voor het hele dashboard: laden / gelukt / mislukt,
// met een expliciete reden bij mislukken (W8-ontwerpregel 1). Geen
// datafetch-bibliotheek: de leeslaag is stateless en de schermen halen per
// filterwijziging één lijst op — daar is een hook genoeg voor.
//
// Twee dingen die deze hook sinds 2026-08-24 bewust WEL doet, allebei uit de
// snelheidsmeting van die dag:
//
//  1. `enabled`. De pagina's zetten `meta` in hun deps omdat ze sectorlabels
//     naar sleutels vertalen. Daardoor haalde élke pagina zijn lijst TWEE keer
//     op bij de eerste load: één keer met meta = null en meteen daarna nog een
//     keer. Op Skills waren dat vier aanroepen in plaats van twee. Een aanroep
//     die toch overgedaan wordt is geen halve meting maar verspilde tijd.
//
//  2. Vorige data vasthouden. Bij elke filterklik verdween de tabel en kwam er
//     "laden…" voor in de plaats. De vraag naar de database duurt een paar
//     honderd milliseconden; een scherm dat in die tijd leegloopt voelt veel
//     trager dan het is. De rijen blijven nu staan en dimmen licht — `stale`
//     zegt dat wat je ziet nog van de vorige selectie is.
//
//  3. Afbreken (A2, 2026-11-01). De fetcher krijgt een `AbortSignal` mee. Bij een
//     nieuw filter, een nieuwe pagina of het verlaten van het scherm breekt de
//     hook de vorige aanvraag af. Zonder dit liepen de oude rondes door en
//     stonden ze in de rij van de server voor de nieuwe (bij Bronnen: 81 s voor
//     twee filterklikken). Een fetcher die het signaal negeert, werkt als voorheen.

import { useCallback, useEffect, useState } from "react";

import { ApiError } from "./api";

export type Resource<T> = {
  data: T | null;
  loading: boolean;
  /** Er staat data, maar hij is van de vorige selectie — er loopt een nieuwe vraag. */
  stale: boolean;
  /** Leesbare reden, klaar om te tonen. Null als er niets misging. */
  error: string | null;
  reload: () => void;
};

export function useResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  /** false = nog niet vragen (bijvoorbeeld: meta is er nog niet). */
  enabled = true,
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return; // wachten is geen fout: `loading` blijft staan
    let alive = true;
    const afbreker = new AbortController();
    setLoading(true);
    setError(null);
    fetcher(afbreker.signal)
      .then((result) => {
        if (!alive) return;
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err.reason : "Onbekende fout bij het ophalen.");
        setLoading(false);
      });
    return () => {
      alive = false;
      afbreker.abort();
    };
    // fetcher wordt per render opnieuw gemaakt; de deps van de aanroeper zijn leidend.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, enabled]);

  return { data, loading, stale: loading && data !== null, error, reload };
}

/**
 * Wacht tot de gebruiker even stil is voordat de waarde doorgaat.
 *
 * Zonder dit vuurt élke toetsaanslag in een zoekveld een ronde naar de
 * database; gemeten kost die ronde ~50 ms per vraag plus het rekenwerk, dus
 * "cybersecurity" intypen zou dertien lijsten ophalen waarvan je er twaalf
 * nooit ziet.
 */
export function useDebounced<T>(value: T, ms = 300): T {
  const [traag, setTraag] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setTraag(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return traag;
}
